<template>
  <div
    class="main-product critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div :class="{ grid: context === 'PDP' }">
      <div
        id="product-gallery"
        class="col xs-span l8"
        data-sign-post-location="main"
        js-sign-post
      >
        <product-gallery
          class="main-product__gallery"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          :layout="context === 'drawer' ? 'tabs' : 'tabs-grid'"
          :loading="state === 'loading'"
          :product="galleryProduct"
          :loop="context === 'PDP'"
          :zoom="context === 'PDP'"
          :variant="variant"
          :pagination="context === 'drawer'"
          :use-loading-animation-from-index="loadingAnimationIndex"
          min-max="360-1320"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </div>

      <div :class="{ 'col xs-span l4': context === 'PDP' }">
        <div
          ref="productSticky"
          class="main-product__details-container"
        >
          <!-- Product Details -->
          <div class="main-product__details">
            <!-- Eyebrow -->
            <component
              :is="eyebrow.url ? 'a' : 'span'"
              v-if="eyebrow"
              :href="eyebrow.url"
              class="text-other-label-medium main-product__eyebrow"
            >
              {{ eyebrow.title }}
            </component>

            <h1
              v-if="braceletFinder && !isMobile"
              class="text-body-l-medium mb-xs"
              v-text="$string('bracelet_finder.step_3_header')"
            />

            <!-- Title -->
            <component
              :is="braceletFinder ? 'h2' : 'h1'"
              id="template-title"
              class="main-product__title text-body-l-regular"
              v-text="product.title"
            />

            <!-- Description -->
            <div
              v-if="product.description && context === 'PDP' && !braceletFinder"
              class="main-product__description text-body-s-regular"
              v-html="product.description"
            />

            <!-- Price -->
            <div class="main-product__price-size-group">
              <product-prices
                class="main-product__prices-container"
                :price-first="false"
                :compare-at-price="price.compareAt"
                :loading="state === 'loading'"
                :price="price.amount"
                class-price="text-body-l-regular"
              />

              <size-guide-btn
                v-if="product.hasOnlyDefaultVariant"
                :product="product"
              />
            </div>

            <p
              v-if="selectedCountry === 'US' && showAffirm"
              class="main-product__affirm affirm-as-low-as"
              data-page-type="product"
              :data-amount="variant.price?.amount || product.price?.amount"
            />
          </div>

          <!-- Siblings -->
          <product-siblings
            :refresh-page="section.productSiblingsRefreshPage"
            :siblings="siblings"
            :product="product"
            :bracelet-finder="braceletFinder"
            :stone-images="stoneImages"
            @update-product="updateHandle"
            @update-active-product="updateTemporaryProduct"
          />

          <!-- Form -->
          <product-form
            class="main-product__form"
            :loading="state === 'loading'"
            :product="product"
            :section="section"
            :variant="variant"
            :size-guide-position="context === 'drawer' ? 'below' : 'right'"
            :wishlist-variant-rerender="context === 'drawer'"
            :bracelet-finder="braceletFinder"
            @update-variant="handleVariantUpdate"
          >
            <template #sizeGuide>
              <size-guide-btn :product="product" />
            </template>
          </product-form>

          <!-- Footer -->
          <div
            v-if="
              context === 'PDP' ||
                braceletFinder ||
                Object.keys(detailFields).length ||
                dynamicAccordions.length ||
                $slots.info
            "
            class="main-product__footer"
          >
            <gemstone-drawer
              v-if="context === 'PDP'"
              :product="product"
            />

            <!-- Product details Metafields -->
            <div
              v-if="Object.keys(detailFields).length"
              class="main-product__metafields"
            >
              <table>
                <tr
                  v-for="(value, key) of detailFields"
                  :key="key"
                  class="main-product__metafield"
                >
                  <th
                    class="main-product__metafield-label text-body-s-regular"
                    v-text="`${key}:`"
                  />
                  <td
                    class="main-product__metafield-value text-body-s-regular"
                    v-text="value"
                  />
                </tr>
              </table>
            </div>

            <!-- Accordion -->
            <basic-accordion
              v-if="dynamicAccordions.length && !braceletFinder"
              :accordion-items="dynamicAccordions"
              :initial-active-index="accordionIndex"
            />

            <basic-accordion
              v-if="finderAccordions.length && braceletFinder"
              :accordion-items="finderAccordions"
              :initial-active-index="accordionIndex"
            />

            <!-- Info blocks -->
            <slot name="info" />
          </div>

          <div class="main-product__rebuy-widgets">
            <rebuy-complete-the-look
              v-for="block of rebuyWidgets"
              :key="block.id"
              :widget-id="block.widgetId"
              :product-id="product.id"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
import BasicAccordion from '~async/basic-accordion/basic-accordion'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductSiblings from '~async/product-siblings/product-siblings'
import RebuyCompleteTheLook from '~async/rebuy-complete-the-look/rebuy-complete-the-look'
import SizeGuideBtn from '~async/size-guide-btn/size-guide-btn'

import ProductPrices from '~global/product-prices/product-prices'

import { formatProduct } from '~/helpers/format'
import { getMetafield } from '~/helpers/general'
import { getMetafieldIDs, getVariables } from '~/helpers/graphql'
import { emitUpdateRequestObject } from '~/helpers/vue'
import productQuery from '~graphql/product.gql'

import { mapActions, mapState } from 'vuex'

import { formatEcommerceEvent, track } from '~/helpers/datalayer-actions'
import { getImages } from '~/helpers/product'
import GemstoneDrawer from '~async/gemstone-drawer/gemstone-drawer'

export default {
  name: 'MainProduct',

  components: {
    RebuyCompleteTheLook,
    BasicAccordion,
    ProductSiblings,
    ProductForm,
    ProductGallery,
    ProductPrices,
    SizeGuideBtn,
    GemstoneDrawer,
  },

  inject: {
    context: {
      default: 'PDP',
    },
  },

  provide() {
    return {
      context: this.context,
    }
  },

  props: {
    accordions: {
      type: Array,
      default: () => [],
    },
    additionalMetafields: {
      type: String,
      default: '',
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    mainProduct: {
      type: [Boolean, Object],
      default: false,
    },
    section: {
      type: Object,
      default: () => ({}),
    },
    siblings: {
      type: Object,
      default: null,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    rebuyWidgets: {
      type: Array,
      default: () => [],
    },
    updateHistory: {
      type: Boolean,
      default: true,
    },
    braceletFinder: {
      type: Boolean,
      default: false,
    },
    stoneImages: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      productFormObserver: null,
      currentIndex: 0,
      currentNamespace: 'gallery',
      handle: cnvs.page.handle,
      state: 'loading',
      accordionIndex: -1,
      tempProduct: false,
      loadingAnimationIndex: this.context === 'PDP' ? 1 : 0,
      selectedCountry: window.cnvs.store.country,
    }
  },

  computed: {
    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      headerState: (state) => state.index.scroll.header,
    }),

    /**
     * Compute which product should display in the gallery.
     * @returns {object}
     */
    galleryProduct() {
      const { tempProduct, product } = this
      const media = getImages(product)
      const newProduct = JSON.parse(JSON.stringify(tempProduct || product || {}))
      const newMedia = getImages(newProduct)
      const difference = media.length - newMedia.length

      if (difference > 0) {
        for (let index = 0; index < difference; index++) {
          newMedia.push({})
        }
      } else {
        newMedia.length = media.length
      }

      return newProduct
    },

    /**
     * Compute the product. The product will fallback to liquid if main
     * product is false
     * @returns {Object}
     */
    product() {
      return this.mainProduct ? this.mainProduct : this.liquid
    },

    /**
     * Compute accordions with dynamic content.
     * @returns {*[]}
     */
    dynamicAccordions() {
      const accordions = [...this.accordions]
      accordions.forEach((accordion, index, arr) => {
        const { source } = accordion
        if (!source?.includes('.')) {
          return
        }

        const metafield = getMetafield({
          metaString: source,
          metafields: this.product.metafields,
        })
        arr[index].content = metafield || ''
      })
      return accordions
    },

    /**
     * Compute accordion for bracelet finder.
     * @returns {*[]}
     */
    finderAccordions() {
      if (this.braceletFinder) {
        const metalField = getMetafield({
          metafields: this.product.metafields,
          namespace: 'product_details',
          key: 'metal',
        })
        const metal = metalField.value
        const siblingOptions = this.product.options
        const stoneOption = siblingOptions.find((option) => option.name === 'Stone')?.values[0]
        const stoneOrMetal = stoneOption ? stoneOption : metal
        const stoneInformation = this.stoneImages.find((image) => image.stone_name === stoneOrMetal)
        const accordionTitle = stoneInformation?.stone_accordion_title
        const accordionText = stoneInformation?.stone_accordion_text
        let accordion = []
        if (accordionTitle) {
          accordion = [
            {
              title: accordionTitle,
              content: accordionText,
            },
          ]
        }
        return accordion
      }
      return []
    },

    /**
     * Compute eyebrow link and text object from product tag
     * @returns {{title: string, url: string}|null}
     */
    eyebrow() {
      const product = this.product || { tags: [] }

      if (!product.tags.length) {
        return null
      }

      const tag = product.tags.find((_tag) => _tag.toLowerCase().includes('eyebrow:')) || ''
      const tagParts = tag.replace('eyebrow:', '').split('|')
      const title = tagParts[0]?.trim()
      const handle = tagParts[1]?.trim()

      if (!title) {
        return null
      }

      return {
        url: handle ? `${cnvs.routes.collections}/${handle}` : undefined,
        title,
      }
    },

    /**
     * Compute the additional product details from metafield
     * @returns {{value: String, label: String}[]}
     */
    detailFields() {
      const additionalDetails =
        getMetafield({
          metafields: this.product.metafields,
          namespace: 'info',
          key: 'product_details',
        }) || []

      const details = {}

      if (Array.isArray(additionalDetails)) {
        additionalDetails.forEach((detail) => {
          details[detail.label] = detail.value
        })
      }

      this.product.metafields.forEach((metafield) => {
        const detailsObj = metafield.value

        if (metafield.namespace !== 'product_details' || details[detailsObj.label]) {
          return
        }

        details[detailsObj.label] = detailsObj.value
      })

      return details
    },

    /**
     * Compute price of the selected variant or hovered product sibling
     * @returns {{amount: (boolean|PaymentCurrencyAmount|*),
     * compareAt: (boolean|PaymentCurrencyAmount|*)}}
     */
    price() {
      let tempSource = this.tempProduct
      if (tempSource && this.variant) {
        tempSource =
          this.tempProduct.variants.find((variant) => {
            return variant.title === this.variant.title
          }) || this.tempProduct
      }
      const { compareAtPrice, price } = tempSource || this.variant || this.product || {}
      return {
        compareAt: compareAtPrice?.amount,
        amount: price?.amount,
      }
    },

    /**
     * Compute if affirm should display. Affirm has an internal range limit of
     * $100 - $30,000
     * @returns {boolean}
     */
    showAffirm() {
      const context = this.variant || this.product
      const price = context?.price?.amount || 0
      const min = 10000
      const max = 3000000

      return price >= min && price <= max
    },
  },

  watch: {
    /**
     * Watch handle for update to load new product.
     */
    handle(newHandle, oldHandle) {
      this.queryProduct(newHandle, oldHandle)
    },

    /**
     * Watch header state to update position of sticky container.
     */
    headerState() {
      if (!this.$refs?.productSticky?.style) {
        return
      }
      this.$refs.productSticky.style.top =
        this.headerState === 'collapsed' ? 'var(--sticky-margin)' : 'var(--header-group-height)'
    },

    /**
     * Save product in recently viewed products array
     */
    product: {
      handler(product, oldProduct) {
        if (!product) {
          return
        }
        this.tempProduct = product
        this.addRecentlyViewedProduct(product)

        if (product?.price?.amount !== oldProduct?.price?.amount) {
          this.refreshAffirm()
        }
      },
      immediate: true,
    },

    variant(newVariant, oldVariant) {
      if (newVariant?.price?.amount !== oldVariant?.price?.amount) {
        this.refreshAffirm()
      }
    },
  },

  mounted() {
    this.appendMetafields()
    this.setProduct()
    this.setEventListeners()
    this.refreshAffirm()

    cnvs.EventBus.emit('initialize-gorgias-buttons')

    this.$nextTick().then(() => {
      cnvs.EventBus.emit('main-product-update', this.product)
    })

    document.addEventListener('click', this.handleDocumentClick)
    window.addEventListener('scroll', this.handleScroll)
  },

  beforeUnmount() {
    if (!this.braceletFinder) {
      this.productFormObserver.disconnect()
      document.removeEventListener('click', this.handleDocumentClick)
      window.removeEventListener('scroll', this.handleScroll)
    }
  },

  methods: {
    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addRecentlyViewedProduct: 'personalization/addRecentlyViewedProduct',
      openOverlay: 'overlays/open',
    }),

    /**
     * Append the additional metafields from section settings to cnvs variable
     */
    appendMetafields() {
      this.additionalMetafields.split(',').forEach((metaStr) => {
        this.$variable('metafields.product').push(metaStr)
      })
    },

    /**
     * Listen for product requests
     */
    setEventListeners() {
      cnvs.EventBus.listen('main-product-request', () => {
        cnvs.EventBus.emit('main-product-update', this.product)
      })
    },

    /**
     * Set Liquid product object to product.
     * @param {String} [handle] - Optional handle override
     */
    setProduct() {
      if (!this.liquid.handle) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     * @param {String} [oldHandle] - Old product handle
     */
    async queryProduct(handle = this.handle, oldHandle = this.handle) {
      if (handle === false || this.product.handle === handle) {
        return
      }

      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: getVariables({
            handle,
            metafieldIds: [...getMetafieldIDs('product'), ...getMetafieldIDs('variant')],
          }),
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        const newProduct = formatProduct(response)

        if (!newProduct) {
          location.href = `${this.$variable('routes.products')}/${handle}`
          return
        }

        this.$emit('update-request-object', {
          response: newProduct,
          type: 'product',
        })

        cnvs.EventBus.emit('main-product-update', newProduct)

        if (handle !== oldHandle) {
          this.handleVariantUpdate(false)
        }

        this.state = 'ready'
      } catch (error) {
        cnvs.ReportError('Failed to load product', error)
      }
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex

      if (this.context === 'PDP') {
        return
      }

      track({ event: 'quick_add_gallery_clicked', jh_value: newIndex + 1 })
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })
      this.tempProduct = null

      if (!this.updateHistory) {
        return
      }

      const params = new URLSearchParams(document.location.search.substring(1))

      if (variant?.id) {
        params.set('variant', variant.id)
      }

      const url =
        variant?.id && params.toString()
          ? `${location.origin}${location.pathname}?${params.toString()}`
          : `${location.origin}${location.pathname}`

      if (!this.braceletFinder) {
        history.replaceState(
          {
            html: '',
          },
          '',
          url,
        )
      }
    },

    /**
     * Update the current product and the history state
     * @param {Object} product - New product
     */
    updateHandle(product) {
      if (!product) {
        return
      }

      if (this.updateHistory && !this.braceletFinder) {
        history.replaceState(
          {
            html: '',
          },
          '',
          product?.url,
        )
      }

      const eventKey =
        this.context === 'PDP' ? 'sibling_product_selected' : 'quick_add_sibling_product_selected'

      track({ event: eventKey, ...formatEcommerceEvent({ product }) })

      this.handle = product?.handle
      this.accordionIndex = this.accordionIndex === false ? -1 : false
      this.tempProduct = null
    },

    /**
     * Update temp product when a product sibling is hovered.
     * This updates the gallery and the price until the sibling is
     * un-hovered
     * @param product
     */
    updateTemporaryProduct(product) {
      this.loadingAnimationIndex = 0
      this.tempProduct = product
    },

    /**
     * Refresh affirm
     */
    async refreshAffirm() {
      const { affirm } = window
      if (this.selectedCountry !== 'US' || !affirm?.ui || !this.showAffirm) {
        return
      }

      await this.$nextTick()

      affirm.ui.ready(() => {
        affirm.ui.refresh()
      })
    },
  },
}
</script>

<style lang="scss">
@import '~async/product-gallery-modal/product-gallery-modal-overlay';
@import './main-product';
</style>
